import React, { useState } from 'react';
import { Box, Text, Button, VStack, Flex, SimpleGrid } from '@chakra-ui/react';
import {
  scheduleSlotKeyToPickerDate,
  pickerDateToScheduleSlotKey,
  parseUtcMidnightKeyToCalendarParts,
  isLateNightLabel,
} from '@foodfluence/shared';
import type { SchedulingSlot } from 'hooks/useSchedulingAvailability';
import { wizardTokens } from '../styles';
import { getPostDueDate } from '../utils';
import { IN_STORE_TURNAROUND_DAYS } from 'constants/turnaroundConstants';
import FoodfluenceLoader from 'components/FoodfluenceLoader';

interface SlotCardGridProps {
  timeOptions: Map<string, SchedulingSlot[]>;
  selectedDate: Date | null;
  selectedTime: string;
  onSelect: (date: Date, time: string) => void;
  hasMoreSlots: boolean;
  expanded: boolean;
  onShowMore: () => void;
  loading: boolean;
  disabled?: boolean;
  maxTurnaroundDays?: number;
  /** Days a brand push added to the due date; included in each slot's "post by". */
  extensionDays?: number;
  /** Go-live collabs: the deadline a visit on that day gives (getVisitContentDue) instead of visit + turnaround. */
  dueFor?: (visitDate: Date) => Date;
  /** Prefix for the due line ("Draft due by" / "Post by" on go-live collabs). */
  dueLabel?: string;
  timezone?: string;
  timezoneLabel?: string;
}

// A resolved, renderable slot with its date already broken into display parts.
interface SlotCard {
  dateKey: string;
  pickerDate: Date;
  slot: SchedulingSlot;
  month: string;
  day: string;
  weekday: string;
  postDue: string;
  /** Short weekday of the evening this slot belongs to, set only for a slot before 5 AM. */
  nightOf: string | null;
}

const SlotCardGrid: React.FC<SlotCardGridProps> = ({
  timeOptions,
  selectedDate,
  selectedTime,
  onSelect,
  hasMoreSlots,
  expanded,
  onShowMore,
  loading,
  disabled = false,
  maxTurnaroundDays = IN_STORE_TURNAROUND_DAYS,
  extensionDays = 0,
  dueFor,
  dueLabel = 'Content due by',
  timezone = 'UTC',
  timezoneLabel = '',
}) => {
  // Show only the top few "other" slots by default (Figma); "See more times" first reveals the
  // rest of the loaded slots, then — once all local slots are shown — pages in more from backend.
  const DEFAULT_VISIBLE = 3;
  const [showAll, setShowAll] = useState(false);
  const selectedKey = selectedDate ? pickerDateToScheduleSlotKey(selectedDate) : null;
  const dayKeys = Array.from(timeOptions.keys()).sort();

  const cards: SlotCard[] = dayKeys.flatMap((dateKey) => {
    const pickerDate = scheduleSlotKeyToPickerDate(dateKey);
    const slots = timeOptions.get(dateKey) ?? [];
    // Post due = brand's max turnaround window after the visit, resolved to 10:00 store-tz via the
    // shared getPostDueDate — same basis as the submitted reminder, so card and record always agree.
    const postDue = (dueFor ? dueFor(pickerDate) : getPostDueDate(pickerDate, maxTurnaroundDays + extensionDays, timezone))
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: timezone });
    // Read the chip off the KEY's calendar parts, probed at noon UTC. `scheduleSlotKeyToPickerDate`
    // returns *local* midnight, so formatting that in UTC renders the previous day for any venue
    // east of UTC — IST midnight Aug 10 is Aug 9 18:30Z, which chipped as "Aug 9 Sun". Noon has no
    // neighbouring day within any real offset. (pickerDate stays as-is: `onSelect` and `postDue`
    // read its local components, which are already correct.)
    const parts = parseUtcMidnightKeyToCalendarParts(dateKey);
    const atNoonUtc = (offsetDays: number) =>
      new Date(Date.UTC(parts.year, parts.month - 1, parts.day + offsetDays, 12));
    const month = atNoonUtc(0).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
    const day = atNoonUtc(0).toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' });
    const weekday = atNoonUtc(0).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
    // A slot before 5 AM is the tail of the previous evening's service day, so "Thu 12:30 AM" is
    // really Wednesday night. The date chip keeps saying Thursday — that IS the day they walk in,
    // and it has to keep agreeing with the confirmation and the stored visit — so the night the
    // shift belongs to is added alongside it rather than replacing it.
    // Full weekday, not the abbreviation: this reads on its own line under the time, where there is
    // room, and "Saturday night" is unmistakable in a way "Sat night" beside a timestamp is not.
    const nightOfLabel = atNoonUtc(-1).toLocaleDateString('en-US', {
      weekday: 'long',
      timeZone: 'UTC',
    });
    return slots.map((slot) => ({
      dateKey,
      pickerDate,
      slot,
      month,
      day,
      weekday,
      postDue,
      nightOf: isLateNightLabel(slot.time) ? nightOfLabel : null,
    }));
  });

  const isSelectedCard = (c: SlotCard) => selectedKey === c.dateKey && selectedTime === c.slot.time;

  // Hero = the backend's recommended slot; if none is flagged, promote the first slot so the
  // hero region is never empty. Everything else goes in the "Other available times" list.
  const heroIndex = cards.findIndex((c) => c.slot.recommended);
  const hero = cards.length ? cards[heroIndex >= 0 ? heroIndex : 0] : null;
  const others = cards.filter((c) => c !== hero);
  const visibleOthers = showAll ? others : others.slice(0, DEFAULT_VISIBLE);

  // "See more times" does two different jobs: first it reveals the rest of the already-loaded
  // slots, then it pages in more from the backend. Only the second one waits on a request.
  const canRevealLocal = !showAll && others.length > DEFAULT_VISIBLE;
  const canPageBackend = hasMoreSlots && !expanded;
  // `expanded` flips synchronously on click, so `canPageBackend` goes false the instant the button
  // is pressed — the button used to unmount right then, taking its spinner with it and leaving the
  // grid unchanged with no sign a fetch was running. Keep it mounted for the round trip.
  const pagingInFlight = expanded && loading;

  const chip = (c: SlotCard, on: boolean) => (
    <VStack
      spacing="4px"
      w="56px"
      h="72px"
      justify="center"
      borderRadius="12px"
      bg={on ? wizardTokens.ink : '#F2F2F0'}
      color={on ? '#FFFFFF' : wizardTokens.ink}
      flexShrink={0}
      aria-hidden
    >
      <Text fontSize="11px" fontWeight={600} letterSpacing="0.06em" lineHeight="normal">{c.month}</Text>
      <Text textStyle="display" fontSize="28px" lineHeight={1.12}>{c.day}</Text>
      <Text fontSize="11px" fontWeight={600} lineHeight="normal" color={on ? '#C6F432' : '#55555A'}>{c.weekday}</Text>
    </VStack>
  );

  const cardStyle = (on: boolean) => ({
    as: 'button' as const,
    type: 'button' as const,
    role: 'radio',
    'aria-checked': on,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    textAlign: 'left' as const,
    w: '100%',
    p: '12px',
    borderRadius: '16px',
    border: on ? `2px solid ${wizardTokens.ink}` : '1px solid #DCDCD8',
    bg: on ? '#F1FADF' : '#FFFFFF',
    color: wizardTokens.ink,
    cursor: 'pointer',
  });

  const body = (c: SlotCard, big: boolean) => (
    <Box flex={1} minW={0}>
      <Text textStyle="display" fontSize={big ? '32px' : '26px'} lineHeight={1.12} textTransform="none">
        {c.slot.time}
        {timezoneLabel && (
          <Text as="span" fontFamily="display" fontSize="13px" fontWeight={600} sx={{ fontStretch: "100%" }} color="#55555A"> {timezoneLabel}</Text>
        )}
      </Text>
      {/* Its own line, not a suffix: a creator scanning "12:30 AM" under a SUN chip has to see that
          the visit is Saturday night, and an inline tag reads as trailing detail. */}
      {c.nightOf && (
        <Text fontSize="13px" fontWeight={600} color="#A1260F" mt="4px" lineHeight="normal">
          {c.nightOf} night
        </Text>
      )}
      {big && (
        <Text fontSize="14px" color="#3E3E42" mt="4px" lineHeight="normal">
          {c.weekday}, {c.month.charAt(0) + c.month.slice(1).toLowerCase()} {c.day}
        </Text>
      )}
      <Text fontSize="13px" color="#55555A" mt="4px" lineHeight="normal">{dueLabel} {c.postDue}</Text>
    </Box>
  );

  return (
    <VStack
      role="radiogroup"
      aria-label="Time"
      spacing="10px"
      align="stretch"
      width="100%"
      fontFamily="display"
      opacity={disabled ? 0.6 : 1}
      pointerEvents={disabled ? 'none' : 'auto'}
      aria-disabled={disabled}
    >
      {/* Hero recommended slot */}
      {hero && (() => {
        const isSelected = isSelectedCard(hero);
        return (
          <Box {...cardStyle(isSelected)} p="14px" flexDirection="column" alignItems="stretch" gap="10px" onClick={() => onSelect(hero.pickerDate, hero.slot.time)}>
            <Flex
              as="span"
              alignSelf="flex-start"
              h="24px"
              px="10px"
              borderRadius="999px"
              bg={wizardTokens.ink}
              color="#C6F432"
              align="center"
              fontSize="11px"
              fontWeight={600}
              letterSpacing="0.08em"
              textTransform="uppercase"
            >
              Recommended
            </Flex>
            <Flex align="center" gap="12px">
              {chip(hero, isSelected)}
              {body(hero, true)}
            </Flex>
          </Box>
        );
      })()}

      {/* Other available times */}
      {others.length > 0 && (
        <>
          <Text as="h2" m={0} mt="10px" fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#66666A">
            Other available times
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="8px">
            {visibleOthers.map((c) => {
              const isSelected = isSelectedCard(c);
              return (
                <Box
                  key={`${c.dateKey}-${c.slot.time}`}
                  {...cardStyle(isSelected)}
                  onClick={() => onSelect(c.pickerDate, c.slot.time)}
                >
                  {chip(c, isSelected)}
                  {body(c, false)}
                </Box>
              );
            })}
          </SimpleGrid>
        </>
      )}

      {/* End-of-list footer. New slots append below, so the loading signal belongs here rather than
          in a header the creator has already scrolled past. */}
      {pagingInFlight ? (
        <Flex align="center" justify="center" gap={2} py={2}>
          <FoodfluenceLoader size="20px" label="Loading more times" />
          <Text fontSize="14px" color="#55555A">Loading more times…</Text>
        </Flex>
      ) : canRevealLocal || canPageBackend ? (
        <Button
          onClick={() => {
            if (canRevealLocal) setShowAll(true);
            else onShowMore();
          }}
          alignSelf="center"
          h="48px"
          px="18px"
          borderRadius="999px"
          border="1.5px solid #0E0E0E"
          bg="#FFFFFF"
          color={wizardTokens.ink}
          fontFamily="display"
          fontSize="15px"
          fontWeight={600}
          _hover={{ bg: '#F2F2F0' }}
        >
          See more times
        </Button>
      ) : null}
    </VStack>
  );
};

export default SlotCardGrid;
